import { useEffect, useMemo, useState } from "react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import { Ban, Clock, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import type { ManagedUser } from "./types";
import { apiErrorMessage, startTrial, type Trial } from "./trials-api";
import { ACTIVE_PILL, NEUTRAL_PILL } from "./trial-pills";
import {
  addDays,
  clamp,
  diffDays,
  fmtDate,
  startOfDay,
  toDateInputValue,
} from "./trial-dates";
import { Chip, Stepper } from "./trial-stepper";

const NEW_TRIAL_PRESETS = [7, 14, 21, 30] as const;
const EXTEND_PRESETS = [7, 14, 30] as const;
const DEFAULT_NEW_DAYS = 14;
const MIN_LENGTH = 1;
const MAX_LENGTH = 365;
const MAX_EXTEND = 365;

type Props = {
  user: ManagedUser | null;
  trial: Trial | null;
  open: boolean;
  onClose: () => void;
  onExtend: (additionalDays: number) => void;
  onRevoke: () => void;
  onStarted: () => void;
};

// ── Start-trial panel (no existing trial) ────────────────────────────────────

type StartPanelProps = {
  user: ManagedUser;
  open: boolean;
  onClose: () => void;
  onStarted: () => void;
};

const StartTrialPanel = ({ user, open, onClose, onStarted }: StartPanelProps) => {
  const [newStart, setNewStart] = useState<Date>(() => startOfDay(new Date()));
  const [newLength, setNewLength] = useState<number>(DEFAULT_NEW_DAYS);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setNewStart(startOfDay(new Date()));
      setNewLength(DEFAULT_NEW_DAYS);
      setSubmitting(false);
    }
  }, [open]);

  const newEnd = useMemo(() => addDays(newStart, newLength), [newStart, newLength]);

  const handleStart = async () => {
    if (newLength < MIN_LENGTH || newLength > MAX_LENGTH) return;
    const numericId = parseInt(user.id, 10);
    if (isNaN(numericId)) return;
    setSubmitting(true);
    try {
      await startTrial(numericId, newLength, newStart.toISOString());
      toast.success(`${newLength}-day trial started for ${user.email}`);
      onStarted();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to start trial"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-3">
          <DialogTitle>Manage trial</DialogTitle>
          <Badge className={`gap-1.5 ${NEUTRAL_PILL}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            No active trial
          </Badge>
        </div>
        <DialogDescription>
          {user.name ? `${user.name} · ` : ""}
          {user.email}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
          <h3 className="mb-3 text-sm font-semibold">Trial settings</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Trial start
              </Label>
              <Input
                type="date"
                value={toDateInputValue(newStart)}
                onChange={(e) => {
                  const next = e.target.value
                    ? startOfDay(new Date(`${e.target.value}T00:00:00`))
                    : startOfDay(new Date());
                  const length = Math.max(MIN_LENGTH, diffDays(newEnd, next));
                  setNewStart(next);
                  setNewLength(clamp(length, MIN_LENGTH, MAX_LENGTH));
                }}
                disabled={submitting}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Trial end
              </Label>
              <Input
                type="date"
                value={toDateInputValue(newEnd)}
                onChange={(e) => {
                  if (!e.target.value) return;
                  const nextEnd = startOfDay(new Date(`${e.target.value}T00:00:00`));
                  setNewLength(clamp(diffDays(nextEnd, newStart), MIN_LENGTH, MAX_LENGTH));
                }}
                disabled={submitting}
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Length (days)
              </Label>
              <div className="w-1/2 rounded-md border border-border/60 bg-background px-3 py-2 text-sm">
                {newLength}
              </div>
              <p className="text-xs text-muted-foreground">
                Edit either date to set a custom window. Length recalculates automatically.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
          <h3 className="mb-3 text-sm font-semibold">Start trial</h3>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-muted-foreground">Start with</span>
            <Stepper
              value={newLength}
              min={MIN_LENGTH}
              max={MAX_LENGTH}
              onChange={setNewLength}
              disabled={submitting}
            />
            <span className="text-sm text-muted-foreground">days</span>
            <div className="flex flex-wrap gap-2">
              {NEW_TRIAL_PRESETS.map((d) => (
                <Chip key={d} active={newLength === d} onClick={() => setNewLength(d)} disabled={submitting}>
                  {d}
                </Chip>
              ))}
            </div>
          </div>
        </div>
      </div>

      <DialogFooter className="sm:justify-between">
        <span />
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleStart}
            disabled={submitting || newLength < MIN_LENGTH || newLength > MAX_LENGTH}
            className="gap-2"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitting ? "Starting…" : "Start trial"}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
};

// ── Active-trial panel (existing trial) ──────────────────────────────────────

type ActivePanelProps = {
  user: ManagedUser;
  trial: Trial;
  open: boolean;
  onClose: () => void;
  onExtend: (additionalDays: number) => void;
  onRevoke: () => void;
};

const ActiveTrialPanel = ({ user, trial, open, onClose, onExtend, onRevoke }: ActivePanelProps) => {
  const [extendBy, setExtendBy] = useState<number>(0);

  useEffect(() => {
    if (open) setExtendBy(0);
  }, [open, trial.user_id]);

  const daysLeft = trial.days_left;
  const isLocked =
    trial.status === "revoked" || trial.status === "expired" || daysLeft <= 0;
  const pillClass = isLocked ? NEUTRAL_PILL : ACTIVE_PILL;

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-3">
          <DialogTitle>Manage trial</DialogTitle>
          <Badge className={`gap-1.5 ${pillClass}`}>
            {isLocked ? (
              <>
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                Account locked
              </>
            ) : (
              <>
                <Clock className="h-3 w-3" strokeWidth={2.5} />
                {daysLeft} day{daysLeft === 1 ? "" : "s"} left
              </>
            )}
          </Badge>
        </div>
        <DialogDescription>
          {user.name ? `${user.name} · ` : ""}
          {user.email}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
          <h3 className="mb-3 text-sm font-semibold">Trial settings</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Trial start
              </Label>
              <div className="rounded-md border border-border/60 bg-background px-3 py-2 text-sm">
                {fmtDate(trial.trial_start)}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Trial end
              </Label>
              <div className="space-y-1">
                <div className={cn(
                  "rounded-md border px-3 py-2 text-sm transition-colors",
                  extendBy > 0
                    ? "border-amber-300 bg-amber-50 text-amber-900"
                    : "border-border/60 bg-background",
                )}>
                  {fmtDate(trial.trial_end)}
                </div>
                {extendBy > 0 && (
                  <p className="text-xs text-amber-700">
                    → {fmtDate(addDays(new Date(trial.trial_end), extendBy).toISOString())} after extension
                  </p>
                )}
              </div>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Length (days)
              </Label>
              <div className={cn(
                "w-1/2 rounded-md border px-3 py-2 text-sm transition-colors",
                extendBy > 0
                  ? "border-amber-300 bg-amber-50 text-amber-900"
                  : "border-border/60 bg-background",
              )}>
                {trial.length_days}
              </div>
              {extendBy > 0 && (
                <p className="text-xs text-amber-700">
                  → {trial.length_days + extendBy} days after extension
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
          <h3 className="mb-3 text-sm font-semibold">Extend by</h3>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-muted-foreground">Extend with</span>
            <Stepper value={extendBy} min={0} max={MAX_EXTEND} onChange={setExtendBy} />
            <span className="text-sm text-muted-foreground">days</span>
            <div className="flex flex-wrap gap-2">
              {EXTEND_PRESETS.map((d) => (
                <Chip key={d} active={extendBy === d} onClick={() => setExtendBy(d)}>
                  {d}
                </Chip>
              ))}
            </div>
          </div>
        </div>
      </div>

      <DialogFooter className="sm:justify-between">
        <Button
          type="button"
          variant="outline"
          className="gap-2 border-red-500/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={onRevoke}
        >
          <Ban className="h-4 w-4" />
          Revoke trial
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onExtend(extendBy)}
            disabled={extendBy <= 0 || extendBy > MAX_EXTEND}
          >
            {extendBy > 0 ? `Extend ${extendBy} days` : "Save changes"}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
};

// ── Shell ─────────────────────────────────────────────────────────────────────

export const ManageTrialDialog = ({
  user,
  trial,
  open,
  onClose,
  onExtend,
  onRevoke,
  onStarted,
}: Props) => {
  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[806px]">
        {trial ? (
          <ActiveTrialPanel
            user={user}
            trial={trial}
            open={open}
            onClose={onClose}
            onExtend={onExtend}
            onRevoke={onRevoke}
          />
        ) : (
          <StartTrialPanel
            user={user}
            open={open}
            onClose={onClose}
            onStarted={onStarted}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
